<template>
    <BaseTemplate app-name="Liberação de Exames" page-title="">
        <template #content>
            <div class="px-6 pt-6">

            <!-- Banner Message -->
            <BannerMessage 
                v-if="showBanner"
                :type="bannerType"
                :message="bannerMessage"
                @close="showBanner = false"
            />

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6 border border-gray-200 dark:border-gray-700">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Busca</label>
                        <select v-model="searchType" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" :style="{ '--tw-ring-color': 'var(--clinic-btn-bg)' }">
                            <option value="patient_name">Nome do Paciente</option>
                            <option value="patient_cpf">CPF do Paciente</option>
                            <option value="patient_cns">CNS do Paciente</option>
                            <option value="accession_number">Número da OS</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Termo de Busca</label>
                        <input 
                            v-model="searchQuery" 
                            type="text" 
                            placeholder="Digite para buscar..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            :style="{ '--tw-ring-color': 'var(--clinic-btn-bg)' }"
                            @keyup.enter="searchReleases"
                        >
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                        <select v-model="statusFilter" class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" :style="{ '--tw-ring-color': 'var(--clinic-btn-bg)' }">
                            <option value="">Todos</option>
                            <option value="pending">Pendente</option>
                            <option value="released">Liberado</option>
                            <option value="expired">Expirado</option>
                            <option value="revoked">Revogado</option>
                        </select>
                    </div>
                </div>
                <div class="flex gap-2">
                    <button 
                        @click="searchReleases"
                        class="px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 transition-colors"
                        :style="{ backgroundColor: 'var(--clinic-btn-bg)', '--tw-ring-color': 'var(--clinic-btn-bg)' }"
                        @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                        @mouseleave="$event.target.style.backgroundColor = 'var(--clinic-btn-bg)'"
                    >
                        Buscar
                    </button>
                    <button 
                        @click="clearFilters"
                        class="px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white rounded-md hover:bg-gray-700 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                    >
                        Limpar
                    </button>

                </div>
            </div>

            <!-- Releases List -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
                <div class="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between">
                        <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">Lista de Liberações</h2>
                    </div>
                </div>
                
                <div v-if="loading" class="p-6 text-center">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" :style="{ borderColor: 'var(--clinic-btn-bg)' }"></div>
                    <p class="mt-2 text-gray-600 dark:text-gray-400">Carregando...</p>
                </div>

                <div v-else-if="releases.length === 0" class="p-6 text-center">
                    <p class="text-gray-500 dark:text-gray-400">Nenhuma liberação encontrada</p>
                </div>

                <div v-else class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Paciente</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Exame</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">OS</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sincronização</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="release in releases" :key="release.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <div class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ release.patient_name }}</div>
                                        <div v-if="release.patient_cpf" class="text-sm text-gray-500 dark:text-gray-400">{{ formatCPF(release.patient_cpf) }}</div>
                                        <div v-if="release.patient_cns" class="text-sm text-gray-500 dark:text-gray-400">CNS: {{ release.patient_cns }}</div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900 dark:text-gray-100">{{ release.service_name }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900 dark:text-gray-100">{{ release.accession_number || '-' }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm text-gray-900 dark:text-gray-100">{{ release.exam_date }}</div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span :class="getStatusClass(release.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                                        {{ getStatusText(release.status) }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span :class="release.synced_to_portal ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'" class="px-2 py-1 text-xs rounded-full">
                                        {{ release.synced_to_portal ? 'Sincronizado' : 'Pendente' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div class="flex space-x-2">
                                        <button 
                                            v-if="release.status === 'released'"
                                            @click="syncRelease(release)"
                                            :disabled="syncLoading"
                                            class="px-3 py-1 rounded-md text-sm text-white transition-colors"
                                            :style="{ backgroundColor: 'var(--clinic-btn-bg)' }"
                                            @mouseenter="$event.target.style.backgroundColor = 'var(--clinic-btn-bg-hover)'"
                                            @mouseleave="$event.target.style.backgroundColor = 'var(--clinic-btn-bg)'"
                                        >
                                            <RefreshCwIcon v-if="!syncLoading" class="w-4 h-4 inline mr-1" />
                                            <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline mr-1"></div>
                                            Sincronizar
                                        </button>
                                        <button 
                                            v-if="release.can_release_to_web && !release.manually_released_to_web"
                                            @click="releaseToWebDirect(release)"
                                            :disabled="webLoading"
                                            class="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded-md text-sm text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <GlobeIcon v-if="!webLoading" class="w-4 h-4 inline mr-1" />
                                            <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline mr-1"></div>
                                            Liberar Web
                                        </button>
                                        <button 
                                            v-if="release.manually_released_to_web"
                                            @click="revokeWebAccess(release)"
                                            class="px-3 py-1 bg-orange-600 hover:bg-orange-700 rounded-md text-sm text-white transition-colors"
                                        >
                                            <LockIcon class="w-4 h-4 inline mr-1" />
                                            Revogar Web
                                        </button>
                                        <button 
                                            v-if="release.status === 'released'"
                                            @click="revokeRelease(release)"
                                            class="px-3 py-1 bg-red-600 hover:bg-red-700 rounded-md text-sm text-white transition-colors"
                                        >
                                            Revogar
                                        </button>

                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="releases.length > 0" class="px-6 py-3 border-t border-gray-200 dark:border-gray-700">
                    <div class="flex items-center justify-between">
                        <div class="text-sm text-gray-700 dark:text-gray-300">
                            Mostrando {{ pagination.offset + 1 }} a {{ pagination.offset + releases.length }} de {{ pagination.count }} resultados
                        </div>
                        <div class="flex space-x-2">
                            <button 
                                @click="previousPage"
                                :disabled="pagination.offset === 0"
                                class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                Anterior
                            </button>
                            <button 
                                @click="nextPage"
                                :disabled="pagination.offset + releases.length >= pagination.count"
                                class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                            >
                                Próximo
                            </button>
                        </div>
                    </div>
                </div>
            </div>


            <Transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-200 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
            >
                <div v-if="showCreateModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closeCreateModal">
                    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full mx-4 border border-gray-200 dark:border-gray-700 transform transition-all" @click.stop>
                        <!-- Header -->
                        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div class="flex items-center space-x-3">
                                <div class="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                                    <PlusIcon class="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Nova Liberação de Exame</h3>
                            </div>
                            <button 
                                @click="closeCreateModal"
                                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Fechar modal"
                            >
                                <XIcon class="w-4 h-4" />
                            </button>
                        </div>
                        
                        <!-- Content -->
                        <div class="p-4">
                            <!-- Info Banner -->
                            <div class="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <div class="flex items-start space-x-2">
                                    <CheckIcon class="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                    <div class="text-sm text-blue-700 dark:text-blue-300">
                                        <p class="font-medium mb-1">Informação:</p>
                                        <p>Apenas laudos médicos com status "Finalizado" podem ser liberados para o portal do paciente.</p>
                                    </div>
                                </div>
                            </div>
                            
                            <form @submit.prevent="createRelease" class="space-y-4">
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Paciente
                                            <span class="text-red-500">*</span>
                                        </label>
                                        <select 
                                            v-model="createForm.patient_id" 
                                            required
                                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        >
                                            <option value="">Selecione um paciente</option>
                                            <option v-for="patient in patients" :key="patient.id" :value="patient.id">
                                                {{ patient.name }} - {{ formatCPF(patient.cpf) }}
                                            </option>
                                        </select>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Laudo Médico
                                            <span class="text-red-500">*</span>
                                        </label>
                                        <select 
                                            v-model="createForm.medical_report_id" 
                                            required
                                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        >
                                            <option value="">Selecione um laudo</option>
                                            <option v-for="report in medicalReports" :key="report.id" :value="report.id">
                                                {{ report.appointment__accession_number }} - {{ report.appointment__service__descricao }} ({{ report.appointment__patient__name }})
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Estudo PACS
                                            <span class="text-red-500">*</span>
                                        </label>
                                        <select 
                                            v-model="createForm.study_id" 
                                            required
                                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        >
                                            <option value="">Selecione um estudo</option>
                                            <option v-for="study in studies" :key="study.id" :value="study.id">
                                                {{ study.accession_number }} - {{ study.modality }} ({{ formatDate(study.study_date) }})
                                            </option>
                                        </select>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Dias para Expiração
                                        </label>
                                        <input 
                                            v-model="createForm.expiration_days" 
                                            type="number" 
                                            min="1"
                                            max="365"
                                            placeholder="30"
                                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                        >
                                    </div>
                                </div>
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Observações
                                    </label>
                                    <textarea 
                                        v-model="createForm.notes" 
                                        rows="3"
                                        placeholder="Observações sobre a liberação..."
                                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        
                        <!-- Footer -->
                        <div class="flex justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700">
                            <button 
                                @click="closeCreateModal"
                                class="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                            >
                                Cancelar
                            </button>
                            <button 
                                @click="createRelease"
                                :disabled="createLoading"
                                class="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 flex items-center space-x-2"
                            >
                                <CheckIcon v-if="!createLoading" class="w-4 h-4" />
                                <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>{{ createLoading ? 'Criando...' : 'Criar Liberação' }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>


            <Transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-200 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
            >
                <div v-if="showDetailsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closeDetailsModal">
                    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full mx-4 border border-gray-200 dark:border-gray-700 transform transition-all" @click.stop>
                        <!-- Header -->
                        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div class="flex items-center space-x-3">
                                <div class="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                    <EyeIcon class="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Detalhes da Liberação</h3>
                            </div>
                            <button 
                                @click="closeDetailsModal"
                                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Fechar modal"
                            >
                                <XIcon class="w-4 h-4" />
                            </button>
                        </div>
                        
                        <!-- Content -->
                        <div class="p-6">
                            <div v-if="selectedRelease" class="space-y-6">
                                <!-- Patient Info -->
                                <div class="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                                    <div class="flex items-center space-x-3 mb-3">
                                        <UserIcon class="w-5 h-5 text-gray-500" />
                                        <h4 class="font-medium text-gray-900 dark:text-gray-100">Informações do Paciente</h4>
                                    </div>
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Paciente:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedRelease.patient_name }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">CPF:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ formatCPF(selectedRelease.patient_cpf) }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">CNS:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedRelease.patient_cns || 'N/A' }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Exame:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedRelease.service_name }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">OS:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedRelease.accession_number || 'N/A' }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Data do Exame:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ formatDate(selectedRelease.exam_date) }}</span>
                                        </div>
                                    </div>
                                </div>

                                <!-- Release Info -->
                                <div class="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                    <div class="flex items-center space-x-3 mb-3">
                                        <KeyIcon class="w-5 h-5 text-green-600 dark:text-green-400" />
                                        <h4 class="font-medium text-green-900 dark:text-green-100">Credenciais de Acesso</h4>
                                    </div>
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Login:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedRelease.login_identifier }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Senha:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedRelease.password }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Status:</span>
                                            <span :class="getStatusClass(selectedRelease.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                                                {{ getStatusText(selectedRelease.status) }}
                                            </span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Data de Liberação:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedRelease.release_date }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Data de Expiração:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedRelease.expiration_date || 'Permanente (Sem expiração)' }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Número de Acessos:</span>
                                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ selectedRelease.access_count }}</span>
                                        </div>
                                        <div class="flex justify-between">
                                            <span class="text-gray-600 dark:text-gray-400">Sincronizado:</span>
                                            <span :class="selectedRelease.synced_to_portal ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'" class="font-medium">
                                                {{ selectedRelease.synced_to_portal ? 'Sim' : 'Não' }}
                                            </span>
                                        </div>
                                    </div>
                                    <div v-if="selectedRelease.notes" class="mt-3 pt-3 border-t border-green-200 dark:border-green-700">
                                        <div class="flex items-start space-x-2">
                                            <StickyNoteIcon class="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                                            <div>
                                                <span class="text-gray-600 dark:text-gray-400 text-sm">Observações:</span>
                                                <p class="text-gray-900 dark:text-gray-100 text-sm mt-1">{{ selectedRelease.notes }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Footer -->
                        <div class="flex justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700">
                            <button 
                                @click="closeDetailsModal"
                                class="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>




            <Transition
                enter-active-class="transition duration-300 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-200 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
            >
                <div v-if="showWebRevokeModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closeWebRevokeModal">
                    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full mx-4 border border-gray-200 dark:border-gray-700 transform transition-all" @click.stop>
                        <!-- Header -->
                        <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div class="flex items-center space-x-3">
                                <div class="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                                    <LockIcon class="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                </div>
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Revogar Acesso Web</h3>
                            </div>
                            <button 
                                @click="closeWebRevokeModal"
                                class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Fechar modal"
                            >
                                <XIcon class="w-4 h-4" />
                            </button>
                        </div>
                        
                        <!-- Content -->
                        <div class="p-4">
                            <div v-if="selectedRelease" class="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                                <div class="text-sm text-orange-700 dark:text-orange-300">
                                    <p><strong>Paciente:</strong> {{ selectedRelease.patient_name }}</p>
                                    <p><strong>Exame:</strong> {{ selectedRelease.service_name }}</p>
                                    <p><strong>Código Web:</strong> {{ selectedRelease.web_access_code }}</p>
                                </div>
                            </div>
                            
                            <form @submit.prevent="confirmWebRevoke" class="space-y-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Motivo da Revogação
                                    </label>
                                    <textarea 
                                        v-model="webRevokeForm.reason" 
                                        rows="3"
                                        placeholder="Motivo da revogação do acesso web..."
                                        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                                    ></textarea>
                                </div>
                            </form>
                            
                            <div class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <div class="flex items-start space-x-2">
                                    <AlertCircleIcon class="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                                    <div class="text-sm text-red-700 dark:text-red-300">
                                        <p class="font-medium mb-1">Atenção:</p>
                                        <ul class="space-y-1 text-xs">
                                            <li>• O acesso web será imediatamente revogado</li>
                                            <li>• O código de acesso será invalidado</li>
                                            <li>• Esta ação não pode ser desfeita</li>
                                            <li>• O acesso era permanente (sem expiração)</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Footer -->
                        <div class="flex justify-end space-x-3 p-4 border-t border-gray-200 dark:border-gray-700">
                            <button 
                                @click="closeWebRevokeModal"
                                class="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors font-medium rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                            >
                                Cancelar
                            </button>
                            <button 
                                @click="confirmWebRevoke"
                                :disabled="webLoading"
                                class="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 flex items-center space-x-2"
                            >
                                <LockIcon v-if="!webLoading" class="w-4 h-4" />
                                <div v-else class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>{{ webLoading ? 'Revogando...' : 'Revogar Acesso' }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </Transition>
            </div>
        </template>
    </BaseTemplate>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import BaseTemplate from '../templates/BaseTemplate.vue'
import { useExamReleaseStore } from '../../stores/examRelease'
import { useClinicStore } from '../../stores/settings/clinicStore'
import { 
    XIcon, 
    EyeIcon, 
    UserIcon,
    KeyIcon,
    StickyNoteIcon,
    CheckIcon,
    RefreshCwIcon,
    GlobeIcon,
    LockIcon,
    AlertCircleIcon,
} from 'lucide-vue-next'
import BannerMessage from '@/components/BannerMessage.vue';

const examReleaseStore = useExamReleaseStore()
const clinicStore = useClinicStore()

// Reactive data
const loading = ref(false)
const releases = ref([])
const searchType = ref('patient_name')
const searchQuery = ref('')
const statusFilter = ref('')
const showDetailsModal = ref(false)
const showWebRevokeModal = ref(false)
const selectedRelease = ref(null)
const syncLoading = ref(false)
const webLoading = ref(false)
const pagination = ref({
    offset: 0,
    limit: 20,
    count: 0
})

// Banner system
const showBanner = ref(false)
const bannerType = ref('success')
const bannerMessage = ref('')



const webRevokeForm = ref({
    reason: ''
})

// Computed
const searchFilters = computed(() => ({
    search_type: searchType.value,
    query: searchQuery.value,
    status: statusFilter.value
}))

// Banner function
const showBannerMessage = (type, message) => {
    bannerType.value = type
    bannerMessage.value = message
    showBanner.value = true
}

// Methods
const loadReleases = async () => {
    loading.value = true
    try {
        const response = await examReleaseStore.searchReleases({
            offset: pagination.value.offset,
            limit: pagination.value.limit,
            ...searchFilters.value
        })
        releases.value = response.items || []
        pagination.value = {
            offset: response.offset || 0,
            limit: response.limit || 20,
            count: response.total || 0
        }
    } catch (error) {
        console.error('Erro ao carregar liberações:', error)
        showBannerMessage('error', 'Erro ao carregar lista de liberações. Tente novamente.')
    } finally {
        loading.value = false
    }
}



const searchReleases = () => {
    pagination.value.offset = 0
    loadReleases()
}

const clearFilters = () => {
    searchType.value = 'patient_name'
    searchQuery.value = ''
    statusFilter.value = ''
    pagination.value.offset = 0
    loadReleases()
}

const previousPage = () => {
    if (pagination.value.offset > 0) {
        pagination.value.offset -= pagination.value.limit
        loadReleases()
    }
}

const nextPage = () => {
    if (pagination.value.offset + releases.value.length < pagination.value.count) {
        pagination.value.offset += pagination.value.limit
        loadReleases()
    }
}



const closeDetailsModal = () => {
    showDetailsModal.value = false
    selectedRelease.value = null
}

const syncRelease = async (release) => {
    try {
        syncLoading.value = true
        await examReleaseStore.syncRelease(release.id)
        loadReleases()
        showBannerMessage('success', 'Liberação sincronizada com portal com sucesso!')
    } catch (error) {
        console.error('Erro ao sincronizar liberação:', error)
        showBannerMessage('error', 'Erro ao sincronizar liberação. Tente novamente.')
    } finally {
        syncLoading.value = false
    }
}

const revokeRelease = async (release) => {
    if (!confirm('Tem certeza que deseja revogar esta liberação?')) {
        return
    }
    
    try {
        await examReleaseStore.revokeRelease(release.id)
        loadReleases()
        showBannerMessage('success', 'Liberação revogada com sucesso!')
    } catch (error) {
        console.error('Erro ao revogar liberação:', error)
        showBannerMessage('error', 'Erro ao revogar liberação. Tente novamente.')
    }
}

const releaseToWebDirect = async (release) => {
    if (!confirm('Tem certeza que deseja liberar este exame para web? Esta ação é permanente e não pode ser desfeita.')) {
        return
    }
    
    try {
        webLoading.value = true
        
        // Se tem liberação criada, usar o endpoint normal
        if (release.id) {
            await examReleaseStore.releaseToWebDirect(release.id)
        } else {
            // Se não tem liberação, usar o novo endpoint com medical_report_id
            await examReleaseStore.releaseMedicalReportToWebDirect(release.medical_report_id)
        }
        
        loadReleases()
        showBannerMessage('success', 'Exame liberado para web com sucesso! (Permanente)')
    } catch (error) {
        console.error('Erro ao liberar para web:', error)
        showBannerMessage('error', 'Erro ao liberar para web. Tente novamente.')
    } finally {
        webLoading.value = false
    }
}

const revokeWebAccess = (release) => {
    selectedRelease.value = release
    webRevokeForm.value = {
        reason: ''
    }
    showWebRevokeModal.value = true
}

const confirmWebRevoke = async () => {
    try {
        webLoading.value = true
        await examReleaseStore.revokeWebAccess(selectedRelease.value.id, webRevokeForm.value)
        closeWebRevokeModal()
        loadReleases()
        showBannerMessage('success', 'Acesso web revogado com sucesso!')
    } catch (error) {
        console.error('Erro ao revogar acesso web:', error)
        showBannerMessage('error', 'Erro ao revogar acesso web. Tente novamente.')
    } finally {
        webLoading.value = false
    }
}

const closeWebRevokeModal = () => {
    showWebRevokeModal.value = false
    selectedRelease.value = null
    webRevokeForm.value = {
        reason: ''
    }
    webLoading.value = false
}



const getStatusClass = (status) => {
    const baseClasses = 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full'
    const classes = {
        pending: `bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 ${baseClasses}`,
        released: `bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 ${baseClasses}`,
        expired: `bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 ${baseClasses}`,
        revoked: `bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 ${baseClasses}`,
        available: `bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 ${baseClasses}`,
        active: `bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 ${baseClasses}`
    }
    return classes[status] || `bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 ${baseClasses}`
}

const getStatusText = (status) => {
    const texts = {
        pending: 'Pendente',
        released: 'Liberado',
        expired: 'Expirado',
        revoked: 'Revogado',
        available: 'Disponível',
        active: 'Ativo'
    }
    return texts[status] || status
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('pt-BR')
}

const formatCPF = (cpf) => {
    if (!cpf) return ''
    const clean = cpf.replace(/\D/g, '')
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

// Lifecycle
onMounted(() => {
    clinicStore.applyClinicStyles()
    loadReleases()
})

// Adicionar listener para tecla ESC
const handleEscape = (event) => {
    if (event.key === 'Escape') {
        if (showDetailsModal.value) {
            closeDetailsModal()
        }
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
/* Estilos específicos do componente */
</style>